import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import {
  NOTISTACK_FAILURE_OPTIONS,
  NOTISTACK_SUCCESS_OPTIONS,
} from "constants/index";
import {
  StateInfo,
  useCreateTaxRateMutation,
  useDeleteTaxRateMutation,
  useUpdateTaxRateMutation,
} from "features/trading/accounts";
import { useReportSettings } from "./ReportSettingsContext";
import { useStateTaxRate } from "hooks/useStateTaxRate";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

const getStateLabel = (
  state: Pick<StateInfo, "state_abbreviation" | "state_name" | "tax_rate">,
) =>
  `${state.state_abbreviation ?? "--"} - ${state.state_name ?? "Unknown"} - ${state.tax_rate ?? "Not set"}`;

const StrategyTaxStateSetting = () => {
  const { settingsInfo } = useReportSettings();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedState, setSelectedState] = useState<StateInfo | null>(null);
  const [stateInputValue, setStateInputValue] = useState("");

  const {
    clientTaxRate: existingTaxRate,
    states,
    isLoadingStates: isLoading,
  } = useStateTaxRate(settingsInfo.client_id ?? null);
  const [createTaxRate] = useCreateTaxRateMutation();
  const [updateTaxRate] = useUpdateTaxRateMutation();
  const [deleteTaxRate] = useDeleteTaxRateMutation();

  useEffect(() => {
    if (!existingTaxRate || !states.length) {
      setSelectedState(null);
      setStateInputValue("");
      return;
    }
    const preselectedState =
      states.find((state) => state.id === existingTaxRate.state) ?? null;
    setSelectedState(preselectedState);
    setStateInputValue(preselectedState ? getStateLabel(preselectedState) : "");
  }, [existingTaxRate, states]);

  const handleStateChange = async (
    _event: React.SyntheticEvent,
    nextValue: StateInfo | null,
  ) => {
    setSelectedState(nextValue);
    setStateInputValue(nextValue ? getStateLabel(nextValue) : "");

    if (!nextValue) {
      if (existingTaxRate) {
        try {
          await deleteTaxRate({ id: existingTaxRate.id }).unwrap();
          enqueueSnackbar(
            "State tax mapping removed.",
            NOTISTACK_SUCCESS_OPTIONS as any,
          );
        } catch {
          enqueueSnackbar(
            "Failed to remove state tax mapping.",
            NOTISTACK_FAILURE_OPTIONS as any,
          );
        }
      }
      return;
    }
    if (!settingsInfo.client_id) {
      enqueueSnackbar(
        "Please select a client first.",
        NOTISTACK_FAILURE_OPTIONS as any,
      );
      return;
    }
    if (existingTaxRate?.state === nextValue.id) {
      return;
    }

    try {
      if (existingTaxRate) {
        await updateTaxRate({
          id: existingTaxRate.id,
          state: nextValue.id,
        }).unwrap();
      } else {
        await createTaxRate({
          state: nextValue.id,
          client: settingsInfo.client_id ?? null,
          account: null,
        }).unwrap();
      }
      enqueueSnackbar(
        existingTaxRate
          ? "State tax mapping updated."
          : "State tax mapping created.",
        NOTISTACK_SUCCESS_OPTIONS as any,
      );
    } catch {
      enqueueSnackbar(
        "Failed to create state tax mapping.",
        NOTISTACK_FAILURE_OPTIONS as any,
      );
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Select state tax for strategy report
      </Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        <Autocomplete
          options={states}
          loading={isLoading}
          getOptionLabel={getStateLabel}
          value={selectedState}
          inputValue={stateInputValue}
          onInputChange={(_event, nextInputValue) =>
            setStateInputValue(nextInputValue)
          }
          onChange={handleStateChange}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="State" size="small" />
          )}
          clearOnEscape
        />
      </Box>
    </Box>
  );
};

export default StrategyTaxStateSetting;
